'use client';

import React, { useEffect, useRef } from 'react';

interface Step {
    number: string;
    title: string;
    desc: string;
    color: string;
    bgColor: string;
    borderColor: string;
    icon: string;
    detail: string;
}

const steps: Step[] = [
    {
        number: '01',
        title: 'Browse live sessions & bootcamps',
        desc: 'Search by skill, subject, or category. Filter by live slots (book anytime) or bootcamps (fixed timetable). Find what you want to learn.',
        color: 'text-primary',
        bgColor: 'bg-primary/8',
        borderColor: 'border-primary/20',
        icon: '🔍',
        detail: 'Hundreds of live sessions across every category',
    },
    {
        number: '02',
        title: 'Pick your time or enrol in a bootcamp',
        desc: 'For live slots: choose a time that works for you from the instructor\'s available schedule. For bootcamps: pay to enrol and get the full timetable.',
        color: 'text-orange',
        bgColor: 'bg-orange/8',
        borderColor: 'border-orange/20',
        icon: '🗓',
        detail: 'Flexible booking or structured enrolment',
    },
    {
        number: '03',
        title: 'Show up and learn live',
        desc: 'Join your live class at the scheduled time. Interact with your instructor, ask questions, get feedback, and actually learn — not just watch.',
        color: 'text-green',
        bgColor: 'bg-green/8',
        borderColor: 'border-green/20',
        icon: '🔴',
        detail: 'Real-time interaction with your instructor',
    },
    {
        number: '04',
        title: 'Track your progress & grow',
        desc: 'See your session history, track skills you\'ve developed, and keep building. Book more sessions or join the next bootcamp when you\'re ready.',
        color: 'text-purple',
        bgColor: 'bg-purple/8',
        borderColor: 'border-purple/20',
        icon: '🌟',
        detail: 'Your growth journey, all in one place',
    },
];

export default function LearnHowItWorksSection() {
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) entry.target.classList.add('visible');
                });
            },
            { threshold: 0.1 }
        );
        const els = sectionRef.current?.querySelectorAll('.reveal');
        els?.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <section id="how-it-works" ref={sectionRef} className="relative py-20 md:py-28 bg-background overflow-hidden">
            <div className="absolute inset-0 grid-bg pointer-events-none opacity-60" />
            <div className="absolute top-0 right-0 w-[500px] h-[500px] blob-accent opacity-20 translate-x-1/3 -translate-y-1/3 pointer-events-none" />

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
                    <div className="reveal mb-5">
                        <span className="pill-tag border border-primary/20 text-primary bg-primary/8 text-xs uppercase tracking-wider">
                            How it works
                        </span>
                    </div>
                    <h2 className="reveal text-section-title font-extrabold text-foreground mb-4 delay-100">
                        From browsing to learning live — in 4 steps.
                    </h2>
                    <p className="reveal text-lg text-muted-foreground font-body leading-relaxed delay-200">
                        Getting started on Wisly Learn is simple. Here’s how you go from curious to learning in real time.
                    </p>
                </div>

                {/* Steps */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                    {steps.map((step, i) => (
                        <div
                            key={step.number}
                            className="reveal"
                            style={{ transitionDelay: `${i * 100}ms` }}
                        >
                            <div className={`${step.bgColor} border ${step.borderColor} rounded-2xl p-6 h-full flex flex-col relative overflow-hidden group card-hover cursor-pointer`}>
                                {/* Step number watermark */}
                                <div className={`absolute top-4 right-4 text-6xl font-extrabold ${step.color} opacity-10 leading-none select-none`}>
                                    {step.number}
                                </div>

                                {/* Icon */}
                                <div className={`w-12 h-12 ${step.bgColor} border ${step.borderColor} rounded-xl flex items-center justify-center text-2xl mb-5 flex-shrink-0`}>
                                    {step.icon}
                                </div>

                                {/* Step label */}
                                <span className={`text-xs font-bold uppercase tracking-wider ${step.color} mb-2`}>Step {step.number}</span>

                                <h3 className="font-bold text-foreground text-base mb-3 leading-snug">{step.title}</h3>
                                <p className="text-muted-foreground text-sm font-body leading-relaxed flex-1">{step.desc}</p>

                                <div className={`mt-4 pt-4 border-t ${step.borderColor}`}>
                                    <span className={`text-xs font-semibold ${step.color}`}>{step.detail}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Two paths callout */}
                <div className="reveal delay-400 mt-14">
                    <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
                        <h3 className="font-bold text-foreground text-lg mb-6 text-center">Two ways to learn on Wisly</h3>
                        <div className="grid sm:grid-cols-2 gap-5">
                            {/* Path 1: Live Slots */}
                            <div className="bg-orange/5 border border-orange/20 rounded-xl p-5">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-orange/10 rounded-xl flex items-center justify-center text-xl">📅</div>
                                    <div>
                                        <h4 className="font-bold text-foreground text-sm">Book a Live Slot</h4>
                                        <p className="text-orange text-xs font-semibold">Flexible · Book anytime</p>
                                    </div>
                                </div>
                                <p className="text-muted-foreground text-sm font-body leading-relaxed">
                                    Browse instructors, see their available time slots, and book a live session that fits your schedule. Great for one-off sessions or ongoing learning at your own pace.
                                </p>
                                <ul className="mt-3 space-y-1.5">
                                    {['Pick any available time slot', 'Pay per session', 'Direct 1-on-1 or small group', 'Reschedule if needed'].map((item) => (
                                        <li key={item} className="flex items-center gap-2 text-xs text-foreground">
                                            <span className="w-1.5 h-1.5 rounded-full bg-orange flex-shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Path 2: Bootcamp */}
                            <div className="bg-primary/5 border border-primary/20 rounded-xl p-5">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-xl">🚀</div>
                                    <div>
                                        <h4 className="font-bold text-foreground text-sm">Join a Bootcamp or Program</h4>
                                        <p className="text-primary text-xs font-semibold">Structured · Fixed timetable</p>
                                    </div>
                                </div>
                                <p className="text-muted-foreground text-sm font-body leading-relaxed">
                                    Enrol in a bootcamp or institutional program with a fixed schedule. Attend live classes on the days they run, follow a structured curriculum, and graduate with real skills.
                                </p>
                                <ul className="mt-3 space-y-1.5">
                                    {['Fixed timetable of live classes', 'Pay once to enrol', 'Structured curriculum', 'Cohort-based learning'].map((item) => (
                                        <li key={item} className="flex items-center gap-2 text-xs text-foreground">
                                            <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
